import { claimflowApiClient } from './claimflowApiClient';

/**
 * Estimation pre-check (« Contrôle d'une estimation », mahhp 22858).
 *
 * Before the estimation cards (`EstimationCard` « règlement » and
 * `EstimationRecoursCard` « recours ») write anything, they ask VEOS whether the
 * estimation may be saved: origami's ERP proxy slug `boby-ws2-gs-ver-estimation`
 * maps to `GET /boBy/v2/WS2_GS_VER_ESTIMATION`, which answers
 * `data[0] = { VERIF: "OK" | "KO", COMMENT: string | null }` (in the standard
 * VEOS list envelope wrapped by `ErpProxyResponse<T>`). The rule itself
 * (3 000 € / HDI delegation threshold 30 000 €) lives in VEOS — the front end
 * only forwards the values.
 *
 * Query contract (shared with origami's endpoint description + Postman — the
 * keys are a contract with VEOS, renaming one is a breaking change):
 *
 * | key           | value                                                    | card      |
 * |---------------|----------------------------------------------------------|-----------|
 * | `id_sin`      | claim id                                                 | both      |
 * | `estimation`  | `reglement` \| `recours`                                 | both      |
 * | `garantie`    | selected `CD_GAR`                                        | both      |
 * | `perteTotale` | `1` \| `0`                                               | règlement |
 * | `vrade`       | VRADE amount (only when Perte totale is on)              | règlement |
 * | `<CD_PSD>`    | one per visible poste with a non-empty amount, signed as it will be saved (deductions negative) | both |
 * | `total`       | net amount shown on the card                             | both      |
 * | `commentaire` | the comment text                                         | both      |
 *
 * The control is a delegation guard, so it FAILS CLOSED: only `VERIF === 'OK'`
 * (trimmed, case-insensitive) lets the submit through. `KO` blocks with VEOS'
 * `COMMENT` (or a fallback); a missing / unexpected `VERIF` or any technical
 * error blocks too, with an explicit message. Every block carries the alert
 * title « Contrôle VEOS » so the card says where the message comes from.
 */

interface ErpProxyResponse<T> {
  success?: boolean;
  http_status?: number;
  data?: T;
  error?: { code?: string; message?: string } | null;
}

/** One row of `WS2_GS_VER_ESTIMATION`. */
export interface VerEstimationRow {
  VERIF?: string | null;
  COMMENT?: string | null;
}

/** Which card is being saved — the `estimation` contract value. */
export type EstimationKind = 'reglement' | 'recours';

/** The values a card forwards to the pre-check (see the contract above). */
export interface EstimationControlParams {
  /** Claim id → `id_sin`. */
  idSin: string | number;
  /** `reglement` (EstimationCard) | `recours` (EstimationRecoursCard). */
  estimation: EstimationKind;
  /** Selected garantie `CD_GAR` → `garantie` (the cards' `garantie` field key). */
  garantie: string;
  /** Règlement only → `perteTotale` `1` | `0`. Omitted by the recours card. */
  perteTotale?: boolean;
  /** Règlement only → `vrade`, sent only when `perteTotale` is true. */
  vrade?: string;
  /**
   * `CD_PSD` → amount, one entry per visible poste with a non-empty amount,
   * SIGNED exactly as it will be saved (deduction postes negative — see
   * `savedPosteAmount` in `components/deductionPostes.ts`).
   */
  postes: Readonly<Record<string, number>>;
  /** Net amount shown on the card → `total`. */
  total: number;
  /** The comment text → `commentaire` (`''` when an admin hid the field). */
  commentaire: string;
}

/**
 * `ok: true` lets the submit through; otherwise `message` explains the block
 * and `title` (`ESTIMATION_CONTROL_TEXT.alertTitle`, « Contrôle VEOS ») names
 * its source — the cards render both as is in their error `Alert`.
 */
export type EstimationControlResult =
  | { ok: true; message: null }
  | { ok: false; title: string; message: string };

export const VER_ESTIMATION_URL =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-ver-estimation';

/** The contract keys a poste code may never shadow. */
const CONTRACT_KEYS: ReadonlySet<string> = new Set([
  'id_sin',
  'estimation',
  'garantie',
  'perteTotale',
  'vrade',
  'total',
  'commentaire',
]);

/** User-facing messages of the pre-check (exported for the card tests). */
export const ESTIMATION_CONTROL_TEXT = {
  /**
   * Title of the card alert of EVERY blocked pre-check: VEOS' bare `COMMENT`
   * reads like an echo of the user's own comment without it.
   */
  alertTitle: 'Contrôle VEOS',
  koFallback: 'Estimation refusée par le contrôle VEOS.',
  unexpected:
    'Le contrôle VEOS de l’estimation a renvoyé une réponse inattendue. Aucune modification n’a été enregistrée.',
  technical: (detail: string) =>
    `Le contrôle VEOS de l’estimation n’a pas pu être effectué (${detail}). Aucune modification n’a été enregistrée.`,
  technicalFallbackDetail: 'erreur technique',
  posteKeyCollision: (cdPsd: string) =>
    `Le poste « ${cdPsd} » porte un code réservé par le contrôle VEOS.`,
} as const;

/** Two-decimal rounding for the displayed total (no float noise in the URL). */
function roundCents(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Build the `WS2_GS_VER_ESTIMATION` query from the card values, in contract
 * order. Throws when a poste code collides with a contract key — the caller
 * turns that into a block (fail closed) rather than silently dropping a poste
 * VEOS needs to sum.
 */
export function buildEstimationControlQuery(
  params: EstimationControlParams
): Record<string, string> {
  const query: Record<string, string> = {
    id_sin: String(params.idSin),
    estimation: params.estimation,
    garantie: params.garantie,
  };
  if (params.perteTotale !== undefined) {
    query.perteTotale = params.perteTotale ? '1' : '0';
    if (params.perteTotale) {
      query.vrade = params.vrade ?? '';
    }
  }
  for (const [cdPsd, amount] of Object.entries(params.postes)) {
    if (CONTRACT_KEYS.has(cdPsd)) {
      throw new Error(ESTIMATION_CONTROL_TEXT.posteKeyCollision(cdPsd));
    }
    query[cdPsd] = String(amount);
  }
  query.total = String(roundCents(params.total));
  query.commentaire = params.commentaire;
  return query;
}

/**
 * First row of the proxy payload. Defensive about the envelope: the standard
 * list envelope `{ total, data: [row] }`, a bare array `[row]`, or a bare row.
 */
function firstRow(payload: unknown): VerEstimationRow | null {
  if (Array.isArray(payload)) {
    const [first] = payload as unknown[];
    return typeof first === 'object' && first !== null ? (first as VerEstimationRow) : null;
  }
  if (typeof payload !== 'object' || payload === null) return null;
  const inner = (payload as { data?: unknown }).data;
  if (Array.isArray(inner)) return firstRow(inner);
  return 'VERIF' in payload ? (payload as VerEstimationRow) : null;
}

/**
 * The backend / transport message of a failed call (`error.message` of the ERP
 * proxy envelope, the workflow 422 `message`, then the JS error), else a
 * generic « erreur technique ». Shared with the cards' comment POST.
 */
export function requestErrorDetail(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'object' && data !== null) {
      const envelope = data as { error?: { message?: unknown }; message?: unknown };
      if (typeof envelope.error?.message === 'string' && envelope.error.message !== '') {
        return envelope.error.message;
      }
      if (typeof envelope.message === 'string' && envelope.message !== '') {
        return envelope.message;
      }
    }
    if (err instanceof Error && err.message !== '') return err.message;
  }
  return ESTIMATION_CONTROL_TEXT.technicalFallbackDetail;
}

function blocked(message: string): EstimationControlResult {
  return { ok: false, title: ESTIMATION_CONTROL_TEXT.alertTitle, message };
}

export const estimationControlService = {
  /**
   * Run the VEOS pre-check. Never throws: every failure mode (a poste-key
   * collision included) resolves to a blocked result carrying the alert title
   * and the message to show (fail closed).
   */
  async verifyEstimation(params: EstimationControlParams): Promise<EstimationControlResult> {
    let payload: ErpProxyResponse<unknown> | undefined;
    try {
      const query = buildEstimationControlQuery(params);
      const response = await claimflowApiClient.get<ErpProxyResponse<unknown> | undefined>(
        VER_ESTIMATION_URL,
        { params: query }
      );
      payload = response.data;
    } catch (err) {
      return blocked(ESTIMATION_CONTROL_TEXT.technical(requestErrorDetail(err)));
    }

    if (payload?.success !== true) {
      const detail = payload?.error?.message;
      return blocked(
        ESTIMATION_CONTROL_TEXT.technical(
          detail !== undefined && detail !== ''
            ? detail
            : ESTIMATION_CONTROL_TEXT.technicalFallbackDetail
        )
      );
    }

    const row = firstRow(payload.data);
    const verif = typeof row?.VERIF === 'string' ? row.VERIF.trim().toUpperCase() : '';
    if (verif === 'OK') return { ok: true, message: null };
    if (verif === 'KO') {
      const comment = typeof row?.COMMENT === 'string' ? row.COMMENT.trim() : '';
      return blocked(comment !== '' ? comment : ESTIMATION_CONTROL_TEXT.koFallback);
    }
    return blocked(ESTIMATION_CONTROL_TEXT.unexpected);
  },
};
